import React, { useState, useEffect } from 'react';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import GdsClient, { EventResponse, GuestWeddingResponse } from '../../../gds-api-client';
import RightRail, { Route, RouterProvider } from '../../../gs-component-right-rail';
import { Question, EventType, FormProps } from '../../EventTypes';
import { GuestWedding } from '../../GuestWeddingTypes';
import DeleteConfirmation from './DeleteConfirmation';
import SaveConfirmation from './SaveConfirmation';
import DiscardConfirmation from './DiscardConfirmation';
import QuestionForm from './QuestionForm';
import { Household } from '../../HouseholdTypes';
import { normalizeQuestion, emptyQuestion } from '../../helpers/question';
import { trackQuestionInteraction, AnalyticsProps } from '../../helpers/analytics';

interface QuestionRightRail {
  event: EventType | null;
  question: Question | null;
  isOpen: boolean;
  guestWedding: GuestWedding;
  households: Household[];
  onClose: () => void;
  onEscapeKey?: () => void;
  onSuccess: (data: GuestWeddingResponse, variant?: string) => void;
  onEventsUpdate: (events: EventResponse[], variant?: string) => void;
  baseAnalyticProps?: AnalyticsProps;
}

const maxLengthExceededMessage = 'Oops! Please shorten this and try again.';

export const validationSchema = Yup.object().shape({
  text: Yup.string()
    .test({
      message: 'Please enter some question text',
      test(value): boolean {
        const { text } = this.parent;
        if (!text) return value && value !== '';
        return true;
      },
    })
    .max(80, maxLengthExceededMessage),
  answerType: Yup.string(),
  options: Yup.array()
    .of(
      Yup.object().shape({
        text: Yup.string(),
        description: Yup.string(),
      })
    )
    .when('answerType', {
      is: 'Option',
      then: Yup.array().of(
        Yup.object().shape({
          text: Yup.string()
            .max(35, maxLengthExceededMessage)
            .test({
              message: 'Please enter text for the option',
              test(value): boolean {
                const { text } = this.parent;
                if (!text) return value && value !== '';
                return true;
              },
            }),
          description: Yup.string().max(120, maxLengthExceededMessage),
        })
      ),
    }),
});

const QuestionRightRail: React.FC<QuestionRightRail> = ({
  event,
  question,
  guestWedding,
  isOpen,
  onClose,
  onEscapeKey,
  onEventsUpdate,
  households,
  onSuccess,
  baseAnalyticProps = {},
}) => {
  const [initialValues, setInitialValues] = useState(emptyQuestion as FormProps);
  const [isGeneralQuestion, setGeneralQuestion] = useState<boolean>(!event);

  const client = new GdsClient();

  useEffect(() => {
    if (question) {
      setInitialValues(normalizeQuestion(question));
    }
  }, [question]);

  useEffect(() => {
    setGeneralQuestion(!event);
  }, [event]);

  let entry = 'main';

  const deletedOptionMessage =
    'This will permanently delete the option and any answers you’ve already received for it. Your other options (and their answers) will still be available.';
  const editedOptionMessage =
    'The option, and any answers you’ve already received, will be updated to reflect these changes.';

  const onSubmit = async (values: any): Promise<void> => {
    try {
      const formQuestion = { ...values };
      const formType = values.id ? 'edit' : 'add';
      let updatedQuestions;

      if (formQuestion.answerType === 'Text') {
        delete formQuestion.options;
      }

      if (isGeneralQuestion) {
        if (formType === 'add') {
          delete formQuestion.id;
          updatedQuestions = guestWedding.questions ? guestWedding.questions?.concat(formQuestion) : [formQuestion];
        } else {
          updatedQuestions = guestWedding.questions?.map(q => (q.id === formQuestion.id ? formQuestion : q));
        }

        const { data: cerRes } = await client.updateGuestWedding({
          questions: updatedQuestions,
        });
        onSuccess(cerRes);
      } else if (event) {
        if (formType === 'add') {
          updatedQuestions = event.questions ? event.questions.concat(formQuestion) : [formQuestion];
        } else {
          updatedQuestions = event.questions?.map(q => (q.id === formQuestion.id ? formQuestion : q));
        }

        const { data: cerRes } = await client.patchEvent(event.id, { questions: updatedQuestions });
        onEventsUpdate([cerRes]);
      }
      const action = `${formType} question`;
      trackQuestionInteraction(
        {
          selection: action,
          question: formQuestion,
          isGeneralQuestion,
        },
        baseAnalyticProps
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnChange
      validateOnBlur
    >
      {(formProps: FormikProps<FormProps>): JSX.Element => {
        return (
          <RightRail open={isOpen} onEscapeKey={onEscapeKey}>
            <RouterProvider entry={entry}>
              <Route
                screen="main"
                component={
                  <QuestionForm
                    {...formProps}
                    onClose={onClose}
                    question={question}
                    isGeneralQuestion={isGeneralQuestion}
                  />
                }
              />
              <Route screen="discard" component={<DiscardConfirmation {...formProps} onClose={onClose} />} />
              <Route
                screen="delete"
                component={
                  <DeleteConfirmation
                    {...formProps}
                    event={event}
                    households={households}
                    client={client}
                    onClose={onClose}
                    guestWedding={guestWedding}
                    onSuccess={onSuccess}
                    onEventsUpdate={onEventsUpdate}
                    isGeneralQuestion={isGeneralQuestion}
                  />
                }
              />
              <Route
                screen="confirm/delete"
                component={<SaveConfirmation {...formProps} message={deletedOptionMessage} onClose={onClose} />}
              />
              <Route
                screen="confirm/edit"
                component={<SaveConfirmation {...formProps} message={editedOptionMessage} onClose={onClose} />}
              />
            </RouterProvider>
          </RightRail>
        );
      }}
    </Formik>
  );
};

export default QuestionRightRail;
